import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { LAYER_TO_SLOT, type Layer, type Suggestion } from '@wardrobe/shared';
import { api } from '../api.js';
import { Button, Card, ErrorBox, ItemPicker, Loading, Screen, Segmented, num, numFixed, slotLabel} from '../ui.js';
import { FeedbackSheet } from './FeedbackSheet.js';

/**
 * §11: Die Startansicht. Wetter, Top-3-Vorschläge, ein Tap auf „das trage ich heute".
 * Das ist die einzige Ansicht, die täglich benutzt wird — sie muss in unter zwei
 * Sekunden nutzbar sein, deshalb genau eine Abfrage und kein Nachladen in Etappen.
 */
export function Today({ navigate }: { navigate: (to: string) => void }): ReactNode {
  const [occasion, setOccasion] = useState<string | null>(null);
  const [feedbackFor, setFeedbackFor] = useState<string | null>(null);
  const qc = useQueryClient();

  // D-34: Die Anlässe stehen in der Datenbank und sind bearbeitbar — der erste ist
  // die Vorauswahl, damit die Ansicht ohne Zutun etwas zeigt.
  const anlaesse = useQuery({ queryKey: ['occasions'], queryFn: () => api.occasions.list() });
  // Bewusst durchgaengig abgesichert: eine unerwartete Antwort darf die Morgens-Ansicht
  // nicht mit einem weissen Bildschirm beenden.
  const gewaehlt = occasion ?? anlaesse.data?.occasions?.[0]?.id ?? null;

  const rec = useQuery({
    queryKey: ['recommend', gewaehlt],
    queryFn: () => api.recommend(gewaehlt ?? ''),
    enabled: gewaehlt !== null,
  });

  const pending = useQuery({ queryKey: ['pending-feedback'], queryFn: api.pendingFeedback });

  const eingetragen = (): void => {
    void qc.invalidateQueries({ queryKey: ['recommend'] });
    void qc.invalidateQueries({ queryKey: ['pending-feedback'] });
    void qc.invalidateQueries({ queryKey: ['items'] });
  };

  const log = useMutation({
    mutationFn: (s: Suggestion) =>
      api.createWearLog({
        occasion: gewaehlt ?? '',
        outfitId: s.outfitId,
        items: s.items.map((i) => ({ itemId: i.itemId, slot: i.slot })),
      }),
    onSuccess: eingetragen,
  });

  /**
   * D-36: Wer etwas anderes trägt als vorgeschlagen, hatte bisher keinen Weg zu einem
   * Trage-Protokoll — „Heute getragen" am einzelnen Teil schreibt nur den Zustand fort,
   * ohne Wetter und ohne Rückmeldung. Die Lernschleife sah damit ausschließlich Tage, an
   * denen ein Vorschlag wörtlich übernommen wurde. Genau die Tage, an denen die App
   * danebenlag, blieben ihr verborgen.
   */
  const [freieWahl, setFreieWahl] = useState(false);
  const alleTeile = useQuery({
    queryKey: ['items'],
    queryFn: () => api.items(),
    enabled: freieWahl,
  });
  const tragbar = (alleTeile.data ?? []).filter((i) =>
    ['clean', 'worn_ok'].includes(i.lifecycleState),
  );

  const freiesLog = useMutation({
    mutationFn: (itemIds: string[]) =>
      api.createWearLog({
        occasion: gewaehlt ?? '',
        outfitId: null,
        items: itemIds.map((id) => {
          const teil = tragbar.find((t) => t.id === id);
          return { itemId: id, slot: LAYER_TO_SLOT[(teil?.layer ?? 'base') as Layer] };
        }),
      }),
    onSuccess: () => {
      setFreieWahl(false);
      eingetragen();
    },
  });

  const heute = new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <Screen title="Heute" subtitle={heute}>
      {(pending.data?.logs.length ?? 0) > 0 && feedbackFor === null && (
        <Card className="mb-5 ring-1 ring-[var(--color-accent)]/30">
          <p className="text-[15px] font-semibold">Wie war das Outfit von heute?</p>
          <p className="mt-1 text-[13px] text-white/50">
            Zwei Sekunden — davon lernt die Empfehlung fuer morgen.
          </p>
          <Button
            className="mt-3 w-full"
            onClick={() => setFeedbackFor(pending.data?.logs[0] ?? null)}
          >
            Kurz bewerten
          </Button>
        </Card>
      )}

      {feedbackFor !== null && (
        <FeedbackSheet
          wearLogId={feedbackFor}
          onClose={() => {
            setFeedbackFor(null);
            void qc.invalidateQueries({ queryKey: ['pending-feedback'] });
          }}
        />
      )}

      <div className="mb-5">
        <Segmented
          options={(anlaesse.data?.occasions ?? []).map((o) => ({ value: o.id, label: o.label }))}
          value={gewaehlt ?? ''}
          onChange={setOccasion}
        />
      </div>

      {rec.isLoading && <Loading label="Wetter und Vorschläge werden geladen …" />}
      <ErrorBox error={rec.error} />

      {rec.data !== undefined && (
        <>
          <Card className="mb-5 overflow-hidden">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[13px] font-medium text-white/45">gefühlt, gewichtet</p>
                <p className="tnum mt-0.5 text-[3.25rem] leading-[1] font-semibold tracking-tight">
                  {num(rec.data.weather.effectiveApparentC)}
                  <span className="ml-1 align-top text-[1.5rem] text-white/40">°C</span>
                </p>
              </div>
              <div className="mt-1 space-y-1.5 text-right text-[13px] text-white/55">
                <p className="tnum">
                  {rec.data.weather.minApparentC} bis {rec.data.weather.maxApparentC} °C
                </p>
                <p className="tnum">{Math.round(rec.data.weather.rainRisk * 100)} % Regen</p>
                <p className="tnum">bis {rec.data.weather.windMaxKmh} km/h</p>
              </div>
            </div>
            {rec.data.stale && (
              // D-27: veraltete Prognose wird als solche gekennzeichnet, nie als frische
              // ausgegeben (Leitplanke 2).
              <p className="mt-3 rounded-xl bg-[#ff9f0a]/12 px-3 py-2 text-[12px] text-[#ffc266]">
                Wetterdienst gerade nicht erreichbar — Stand von{' '}
                {new Date(rec.data.weather.fetchedAt).toLocaleString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                .
              </p>
            )}
            {/* Die Fenster sind der Kern des Exposure-Modells (§5) — sie zeigen, worauf
                die gewichtete Zahl oben beruht. */}
            <div className="mt-4 grid grid-cols-3 gap-2">
              {rec.data.weather.windows.map((w) => (
                <div key={w.label} className="rounded-xl bg-white/8 px-2.5 py-2 text-center">
                  <p className="text-[11px] text-white/40">{w.from}–{w.to}</p>
                  <p className="tnum mt-0.5 text-[15px] font-semibold">{w.apparentC}°</p>
                </div>
              ))}
            </div>
          </Card>

          {rec.data.suggestions.length === 0 && (
            <Card>
              <p className="text-[15px] font-semibold">
                Für diesen Anlass ist gerade nichts Tragbares verfügbar.
              </p>
              <p className="mt-2 text-[13px] leading-relaxed text-white/50">
                Das kann an der Wäsche liegen oder daran, dass noch zu wenig erfasst ist —
                für einen Vorschlag braucht es mindestens Oberteil, Hose und Schuhe.
              </p>
              <Button className="mt-3 w-full" onClick={() => navigate('/schrank/neu')}>
                Teil erfassen
              </Button>
            </Card>
          )}

          <div className="space-y-4">
            {rec.data.suggestions.map((s, index) => (
              <SuggestionCard
                key={s.items.map((i) => i.itemId).join()}
                suggestion={s}
                rank={index + 1}
                busy={log.isPending}
                onWear={() => log.mutate(s)}
              />
            ))}
          </div>

          {freieWahl ? (
            <div className="mt-4">
              {alleTeile.isLoading ? (
                <Loading label="Schrank wird geladen …" />
              ) : (
                <ItemPicker
                  titel="Was hast du heute wirklich an?"
                  hinweis="Auch eine eigene Zusammenstellung wird mit dem Wetter von heute protokolliert — und abends fragt die App danach. Ohne diesen Weg lernt sie nur an den Tagen, an denen du einen Vorschlag übernommen hast."
                  kandidaten={tragbar}
                  vorausgewaehlt={new Set(rec.data.suggestions[0]?.items.map((i) => i.itemId) ?? [])}
                  busy={freiesLog.isPending}
                  onAbbrechen={() => setFreieWahl(false)}
                  onSpeichern={(ids) => {
                    if (ids.length > 0) freiesLog.mutate(ids);
                  }}
                  speichernLabel={(n) => (n === 0 ? 'Nichts ausgewählt' : `${n} Teile eintragen`)}
                />
              )}
              <ErrorBox error={freiesLog.error} />
            </div>
          ) : (
            <Button variant="ghost" className="mt-4 w-full" onClick={() => setFreieWahl(true)}>
              Ich trage etwas anderes
            </Button>
          )}

          <ErrorBox error={log.error} />
          {log.isSuccess && (
            <p className="mt-4 rounded-[var(--radius-card)] bg-[#30d158]/12 p-3.5 text-[14px] text-[#5ee27f]">
              Eingetragen. Heute Abend fragt die App nach, wie es war.
            </p>
          )}

          <p className="mt-8 pb-2 text-center text-[11px] text-white/25">{rec.data.attribution}</p>
        </>
      )}
    </Screen>
  );
}

function SuggestionCard({ suggestion, rank, onWear, busy }: {
  suggestion: Suggestion;
  rank: number;
  onWear: () => void;
  busy: boolean;
}): ReactNode {
  const [showTerms, setShowTerms] = useState(false);
  const teile = suggestion.items.map((i) => i.name);

  return (
    <Card className={rank === 1 ? 'ring-1 ring-white/10' : ''}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {/* Der erste Vorschlag ist der Vorschlag; die anderen sind Alternativen. */}
          <span
            className={`tnum grid size-6 place-items-center rounded-full text-[12px] font-bold ${
              rank === 1 ? 'bg-white text-black' : 'bg-white/10 text-white/60'
            }`}
          >
            {rank}
          </span>
          {!suggestion.generated && (
            <span className="rounded-full bg-[#bf5af2]/15 px-2 py-0.5 text-[11px] font-semibold text-[#d9a2f7]">
              gespeichertes Outfit
            </span>
          )}
        </div>
        <span className="tnum rounded-full bg-white/8 px-2.5 py-1 text-[11px] font-semibold text-white/55">
          Wohlfühlpunkt {num(suggestion.comfortCenterC)} °C
        </span>
      </div>

      {suggestion.outfitName !== null && (
        <p className="mt-3 text-[17px] font-semibold tracking-tight">{suggestion.outfitName}</p>
      )}

      {/* Ein Teil je Zeile statt einer Aufzaehlung: beim Anziehen liest man eine Liste
          ab, keinen Fliesstext. */}
      <ul className="mt-3 space-y-1.5">
        {teile.map((name, i) => (
          <li key={name} className="flex items-center gap-2.5 text-[15px]">
            <span className="size-1.5 shrink-0 rounded-full bg-white/25" aria-hidden />
            <span className="truncate">{name}</span>
            <span className="ml-auto shrink-0 text-[11px] text-white/30">
              {slotLabel(suggestion.items[i]?.slot ?? '')}
            </span>
          </li>
        ))}
      </ul>

      {/* §6 Schritt 5: Erklärbarkeit ist Pflicht. Kein Vorschlag ohne Begründung. */}
      <p className="mt-4 rounded-xl bg-white/4 p-3 text-[13.5px] leading-relaxed text-white/65">
        {suggestion.reasoning}
      </p>

      {suggestion.warnings.map((w) => (
        <p key={w} className="mt-2 rounded-xl bg-[#ff9f0a]/12 p-2.5 text-[12px] text-[#ffc266]">
          {w}
        </p>
      ))}

      <div className="mt-4 flex gap-2">
        <Button className="flex-1" onClick={onWear} disabled={busy}>
          Das trage ich heute
        </Button>
        <Button variant="ghost" onClick={() => setShowTerms(!showTerms)}>
          {showTerms ? 'Weniger' : 'Warum?'}
        </Button>
      </div>

      {showTerms && (
        <ul className="mt-4 space-y-2 border-t border-white/8 pt-3 text-[12.5px] text-white/50">
          {suggestion.terms.map((t) => (
            <li key={t.key} className="flex justify-between gap-3">
              <span>{t.explanation}</span>
              <span
                className={`tnum shrink-0 font-semibold ${
                  t.contribution < 0 ? 'text-[#ff9f9a]' : t.contribution > 0 ? 'text-[#5ee27f]' : 'text-white/30'
                }`}
              >
                {t.contribution > 0 ? '+' : ''}
                {numFixed(t.contribution)}
              </span>
            </li>
          ))}
          <li className="flex justify-between gap-3 border-t border-white/8 pt-2 font-semibold text-white/75">
            <span>Summe</span>
            <span className="tnum">{numFixed(suggestion.score)}</span>
          </li>
        </ul>
      )}
    </Card>
  );
}
